import { useContext, useState } from 'react';
import { useRouter } from 'next/router';
import { Textarea, useDisclosure } from '@chakra-ui/react';
import { inflect } from 'inflection';
import { CbButton } from '../Buttons/CbButton';
import { Flipped } from 'react-flip-toolkit';
import arcadeApiClient from 'apiClients/arcadeApiClient';
import { AppContext, NewsfeedContext } from 'providers';
import styles from './CbNewsfeedAnnouncementForm.module.css';
import { CbRichAnnouncementModal } from './CbRichAnnouncementModal';
import classnames from 'classnames';
interface CbNewsfeedAnnouncementFormProps {
  onPost?: () => void;
  teamId?: number;
  onFocus?: (focused: boolean) => void;
}
export const CbNewsfeedAnnouncementForm = ({
  onPost: onPostSuccess,
  onFocus
}: CbNewsfeedAnnouncementFormProps) => {
  const router = useRouter();
  const [content, setContent] = useState('');
  const [inputFocused, setInputFocusedRaw] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const {
    setAdditionalFeeds,
    teamId
  } = useContext(NewsfeedContext);
  const {
    isOpen: richNewsfeedModalOpen,
    onClose: closeRichNewsfeedModal,
    onOpen: showRichNewsfeedModal
  } = useDisclosure();
  const {
    setToast
  } = useContext(AppContext);
  const handleCloseModal = () => {
    closeRichNewsfeedModal();
    setAdditionalFeeds([]);
  };
  const setInputFocused = (focused: boolean) => {
    onFocus?.(focused);
    setInputFocusedRaw(focused);
  };
  const submitAnnouncement = params => {
    setIsPosting(true);

    /*
      The teamId being nil means we are posting from the global feed.
      team_ids having values means that the user is posting to specific team feeds.
      This combination means that they won't immediately see the item they posted so let's pick
      one of the teams and swap to that feed.
    */
    const {
      team_id: teamIds
    } = params.announcement;

    /*
      teamId is pulled from the URL and is only set if we are viewing a team feed
    */
    const postingFromGlobal = !teamId;

    /*
      Values are added to this array when either:
      * A team is selected from the additional feeds selector
      * The announcement modal is opened on a team feed
    */
    const postingToTeams = teamIds.length > 0;
    const description = postingToTeams ? `Post created for ${teamIds.length} ${inflect('team', teamIds.length)}` : 'Global Post created';
    return arcadeApiClient.post('/multi_platform/newsfeed/announce', params).then(_success => {
      setContent('');
      setInputFocused(false);
      onPostSuccess?.();
      setToast({
        status: 'success',
        description
      });
      if (postingFromGlobal && postingToTeams) {
        const feedToSwapTo = teamIds[0];
        router.push(`team?team=${feedToSwapTo}`);
      }
    }).catch(e => {
      setToast({
        status: 'error',
        description: `Could not Post Announcement: ${e}`
      });
    }).finally(() => setIsPosting(false));
  };

  /* Set focused state to false if no content (i.e. not posting)
     If there is content, leave input state focused  */
  const onInputBlur = () => {
    if (content) return;
    setInputFocused(false);
  };
  const onPostSubmitted = ({
    text,
    signed_blob_ids,
    preview_url,
    image_url,
    team_ids
  }) => submitAnnouncement({
    announcement: {
      text: text,
      team_id: [teamId, ...team_ids].filter(id => id),
      signed_blob_ids: signed_blob_ids,
      url_for_preview: preview_url,
      image_url: image_url
    }
  }).then(handleCloseModal);
  return <>
      <Flipped flipId="form" data-sentry-element="Flipped" data-sentry-source-file="CbNewsfeedAnnouncementForm.tsx">
        <div data-testid="newsfeed-announcement-form" className={`${styles.form} ${inputFocused ? styles.active : ''}`}>
          <Flipped flipId="input" data-sentry-element="Flipped" data-sentry-source-file="CbNewsfeedAnnouncementForm.tsx">
            <Textarea borderColor="cloud.300" className={classnames([styles.input, styles.richNewsfeedInput])} onBlur={onInputBlur} onChange={event => setContent(event.target.value)} onFocus={() => showRichNewsfeedModal()} placeholder="What do you want to say?" value={content} data-sentry-element="Textarea" data-sentry-source-file="CbNewsfeedAnnouncementForm.tsx" />
          </Flipped>
          <Flipped flipId="postButton" data-sentry-element="Flipped" data-sentry-source-file="CbNewsfeedAnnouncementForm.tsx">
            <CbButton className={styles.submit} onClick={showRichNewsfeedModal} data-sentry-element="CbButton" data-sentry-source-file="CbNewsfeedAnnouncementForm.tsx">
              Post
            </CbButton>
          </Flipped>
        </div>
      </Flipped>
      {richNewsfeedModalOpen && <CbRichAnnouncementModal isPosting={isPosting} onClose={handleCloseModal} onPost={onPostSubmitted} />}
    </>;
};